import { createFileRoute, Link } from '@tanstack/react-router';
import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

export const Route = createFileRoute('/privacy')({
  component: PrivacyPolicy,
});

function PrivacyPolicy() {
  const { t } = useLingui();
  const lastUpdated = 'February 10, 2026';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link
            to="/"
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span className="text-sm">
              <Trans>Back to TrackMyGroceries</Trans>
            </span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="container max-w-4xl mx-auto px-4 py-12">
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
          <h1 className="font-display text-4xl font-bold mb-2">
            <Trans>Privacy Policy</Trans>
          </h1>
          <p className="text-muted-foreground mb-8">{t`Last updated: ${lastUpdated}`}</p>

          <div className="prose prose-slate dark:prose-invert max-w-none">
            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Introduction</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>
                  TrackMyGroceries (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) is committed
                  to protecting your privacy. This Privacy Policy explains how we collect, use,
                  disclose, and safeguard your information when you use our mobile application and
                  web service (collectively, the &quot;Service&quot;).
                </Trans>
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                <Trans>
                  We are based in Canada and comply with the Personal Information Protection and
                  Electronic Documents Act (PIPEDA). For users in the European Union, we also comply
                  with the General Data Protection Regulation (GDPR).
                </Trans>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Information We Collect</Trans>
              </h2>

              <h3 className="font-semibold text-lg mt-6 mb-3">
                <Trans>Account Information</Trans>
              </h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>
                  <Trans>Email address (required for account creation)</Trans>
                </li>
                <li>
                  <Trans>Display name (optional)</Trans>
                </li>
                <li>
                  <Trans>Profile picture (optional)</Trans>
                </li>
                <li>
                  <Trans>Password (securely hashed, never stored in plain text)</Trans>
                </li>
              </ul>

              <h3 className="font-semibold text-lg mt-6 mb-3">
                <Trans>Food Inventory Data</Trans>
              </h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>
                  <Trans>Products you add to your inventory (names, barcodes, images)</Trans>
                </li>
                <li>
                  <Trans>Purchase dates, expiry dates, and quantities</Trans>
                </li>
                <li>
                  <Trans>Storage locations you create</Trans>
                </li>
                <li>
                  <Trans>Consumption and waste tracking data</Trans>
                </li>
              </ul>

              <h3 className="font-semibold text-lg mt-6 mb-3">
                <Trans>Household Information</Trans>
              </h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>
                  <Trans>Household names and member lists</Trans>
                </li>
                <li>
                  <Trans>Invitation records</Trans>
                </li>
                <li>
                  <Trans>Member roles and permissions</Trans>
                </li>
              </ul>

              <h3 className="font-semibold text-lg mt-6 mb-3">
                <Trans>Technical Information</Trans>
              </h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>
                  <Trans>Device type and operating system</Trans>
                </li>
                <li>
                  <Trans>App version</Trans>
                </li>
                <li>
                  <Trans>Error logs and crash reports (for improving the Service)</Trans>
                </li>
                <li>
                  <Trans>Usage patterns (anonymized analytics)</Trans>
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>How We Use Your Information</Trans>
              </h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>
                  <Trans>To provide and maintain the Service</Trans>
                </li>
                <li>
                  <Trans>To send you expiry notifications and reminders (if enabled)</Trans>
                </li>
                <li>
                  <Trans>To enable household sharing features</Trans>
                </li>
                <li>
                  <Trans>To improve and optimize the Service</Trans>
                </li>
                <li>
                  <Trans>To respond to your support requests</Trans>
                </li>
                <li>
                  <Trans>To process payments for premium features</Trans>
                </li>
                <li>
                  <Trans>To comply with legal obligations</Trans>
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Third-Party Services</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                <Trans>
                  We use the following third-party services to operate TrackMyGroceries:
                </Trans>
              </p>

              <div className="space-y-4">
                <div className="rounded-xl border p-4">
                  <h4 className="font-semibold">Supabase</h4>
                  <p className="text-sm text-muted-foreground">
                    <Trans>
                      Database hosting and authentication. Your data is stored securely on Supabase
                      servers.
                    </Trans>{' '}
                    <a
                      href="https://supabase.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      <Trans>Supabase Privacy Policy</Trans>
                    </a>
                  </p>
                </div>

                <div className="rounded-xl border p-4">
                  <h4 className="font-semibold">Open Food Facts</h4>
                  <p className="text-sm text-muted-foreground">
                    <Trans>
                      Product database for barcode lookups. When you scan a barcode, we query this
                      public database.
                    </Trans>{' '}
                    <a
                      href="https://world.openfoodfacts.org/terms-of-use"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      <Trans>Open Food Facts Terms</Trans>
                    </a>
                  </p>
                </div>

                <div className="rounded-xl border p-4">
                  <h4 className="font-semibold">Sentry</h4>
                  <p className="text-sm text-muted-foreground">
                    <Trans>
                      Error tracking and monitoring. Helps us identify and fix bugs. May include
                      device information and error context.
                    </Trans>{' '}
                    <a
                      href="https://sentry.io/privacy/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      <Trans>Sentry Privacy Policy</Trans>
                    </a>
                  </p>
                </div>

                <div className="rounded-xl border p-4">
                  <h4 className="font-semibold">
                    <Trans>Paddle (Web Payments)</Trans>
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    <Trans>
                      Processes payments for premium subscriptions on web. Paddle acts as the
                      Merchant of Record.
                    </Trans>{' '}
                    <a
                      href="https://www.paddle.com/legal/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      <Trans>Paddle Privacy Policy</Trans>
                    </a>
                  </p>
                </div>

                <div className="rounded-xl border p-4">
                  <h4 className="font-semibold">
                    <Trans>Adapty (Mobile Payments)</Trans>
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    <Trans>
                      Manages in-app subscriptions for iOS and Android through Apple App Store and
                      Google Play.
                    </Trans>{' '}
                    <a
                      href="https://adapty.io/privacy-policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      <Trans>Adapty Privacy Policy</Trans>
                    </a>
                  </p>
                </div>

                <div className="rounded-xl border p-4">
                  <h4 className="font-semibold">PostHog</h4>
                  <p className="text-sm text-muted-foreground">
                    <Trans>
                      Product analytics and feature flags. Collects anonymized usage data to help us
                      improve the app experience. No personally identifiable information is shared.
                    </Trans>{' '}
                    <a
                      href="https://posthog.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      <Trans>PostHog Privacy Policy</Trans>
                    </a>
                  </p>
                </div>

                <div className="rounded-xl border p-4">
                  <h4 className="font-semibold">DeepL</h4>
                  <p className="text-sm text-muted-foreground">
                    <Trans>
                      Machine translation service. Product names and UI text may be sent to DeepL
                      for translation into your preferred language. No personal data is included in
                      translation requests.
                    </Trans>{' '}
                    <a
                      href="https://www.deepl.com/en/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      <Trans>DeepL Privacy Policy</Trans>
                    </a>
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Data Sharing</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>
                  We do not sell your personal information. We only share data in these
                  circumstances:
                </Trans>
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                <li>
                  <Trans>
                    <strong>With household members:</strong> When you join or create a household,
                    other members can see shared inventory data.
                  </Trans>
                </li>
                <li>
                  <Trans>
                    <strong>With service providers:</strong> The third-party services listed above,
                    solely to provide the Service.
                  </Trans>
                </li>
                <li>
                  <Trans>
                    <strong>For legal reasons:</strong> If required by law or to protect our rights.
                  </Trans>
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Data Retention</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>
                  We retain your data for as long as your account is active. If you delete your
                  account, we will delete your personal data within 30 days, except where we are
                  required to retain it for legal or legitimate business purposes.
                </Trans>
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                <Trans>
                  Anonymized, aggregated data (such as overall waste statistics) may be retained
                  indefinitely for research and product improvement.
                </Trans>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Your Rights</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                <Trans>You have the following rights regarding your personal information:</Trans>
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>
                  <Trans>
                    <strong>Access:</strong> Request a copy of your data
                  </Trans>
                </li>
                <li>
                  <Trans>
                    <strong>Correction:</strong> Update inaccurate information
                  </Trans>
                </li>
                <li>
                  <Trans>
                    <strong>Deletion:</strong> Request deletion of your account and data
                  </Trans>
                </li>
                <li>
                  <Trans>
                    <strong>Portability:</strong> Export your data in a common format
                  </Trans>
                </li>
                <li>
                  <Trans>
                    <strong>Withdraw consent:</strong> Opt out of optional data processing
                  </Trans>
                </li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                <Trans>To exercise these rights, contact us at</Trans>{' '}
                <a
                  href="mailto:privacy@trackmygroceries.com"
                  className="text-primary hover:underline"
                >
                  privacy@trackmygroceries.com
                </a>
                .
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Security</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>
                  We implement appropriate technical and organizational measures to protect your
                  data, including:
                </Trans>
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                <li>
                  <Trans>Encryption in transit (HTTPS/TLS)</Trans>
                </li>
                <li>
                  <Trans>Encryption at rest for sensitive data</Trans>
                </li>
                <li>
                  <Trans>Secure password hashing</Trans>
                </li>
                <li>
                  <Trans>Row-level security for multi-tenant data isolation</Trans>
                </li>
                <li>
                  <Trans>Regular security reviews</Trans>
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Children&apos;s Privacy</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>
                  TrackMyGroceries is not intended for children under 13 years of age. We do not
                  knowingly collect personal information from children under 13. If you believe we
                  have collected information from a child under 13, please contact us immediately.
                </Trans>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Changes to This Policy</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>
                  We may update this Privacy Policy from time to time. We will notify you of
                  significant changes by posting the new policy on this page and updating the
                  &quot;Last updated&quot; date. For material changes, we may also notify you via
                  email or in-app notification.
                </Trans>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Contact Us</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>
                  If you have questions about this Privacy Policy or our data practices, please
                  contact us:
                </Trans>
              </p>
              <div className="mt-4 rounded-xl border p-4">
                <p className="font-semibold">TrackMyGroceries</p>
                <p className="text-muted-foreground">
                  <Trans>Email:</Trans>{' '}
                  <a
                    href="mailto:privacy@trackmygroceries.com"
                    className="text-primary hover:underline"
                  >
                    privacy@trackmygroceries.com
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="container max-w-4xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} TrackMyGroceries. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <Link to="/privacy" className="hover:text-foreground">
              <Trans>Privacy Policy</Trans>
            </Link>
            <Link to="/terms" className="hover:text-foreground">
              <Trans>Terms of Service</Trans>
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
